/**
 * 굿즈 상세(FR-GOODS / docs/build/16) — 웹·엣지·관리자·AI초안(P2)이 공유하는 단일 정의.
 *
 * 설계 메모:
 * - 결제·배송·문의는 전부 외부 스마트스토어. 여기 타입에 PII 필드는 **없다**(불변식 1 무수집).
 * - 이미지는 기존 에셋 매니페스트 슬롯(`goods.<id>.<n>`)을 재사용 → 업로드·검증·롤백(prev)·실시간 푸시를 그대로 얻는다.
 * - 필드 이름은 docs/build/16 §4-3 `synthesize` 출력 스키마와 1:1 — P2(AI 초안)는 배선만 하면 되게.
 */

// 🔴 확장자 — scripts/check-owner.mjs 가 node 로 직접 읽는다(storeLinks 를 아이 화면과 같이 쓰려고)
import { isPlaceholderUrl } from "./placeholder.ts";

export const GOODS_BADGES = ["신상", "인기", "한정"] as const;
export type GoodsBadge = (typeof GOODS_BADGES)[number];

/** 상품 1개. tagline/desc 는 톤이 다른 두 독자용(아이 / 보호자)이라 별도 필드로 나눈다. */
export interface GoodsProduct {
  id: string; // [a-z0-9_] — /goods/:id 라우트·슬롯 키에 그대로 쓰이므로 안전문자만
  name_ko: string;
  price?: number; // 정보성 표기(결제는 스토어). 미설정 시 가격 숨김
  badge?: GoodsBadge;
  tagline_child_ko?: string; // 아이 한 줄(크게, 쉬운 말)
  desc_guardian_ko?: string; // 보호자 설명(소재·크기·주의)
  images: string[]; // 매니페스트 슬롯 키(goods.<id>.<n>), 대표=[0]
  store_url?: string; // (예전 형식) 스토어 한 곳. 아래 stores 로 합쳐 읽는다 — 직접 읽지 말 것
  /**
   * 살 수 있는 곳 — **최대 5곳**.
   *
   * 한 상품을 여러 곳에서 파는 게 보통이다(스마트스토어·쿠팡·자사몰·오프라인 안내…).
   * 넣은 만큼만 버튼이 생긴다 — **한 곳만 넣으면 버튼도 하나**다. 빈 자리는 아무것도 안 그린다.
   *
   * 예전 문서에는 `store_url` 하나뿐이었다. 읽을 때 `storeLinks()` 가 둘을 합쳐 주므로
   * 옛 게시본도 그대로 동작한다 — 화면과 검증은 **항상 `storeLinks()` 만** 쓴다.
   */
  stores?: GoodsStore[];
  soldout?: boolean;
  tags?: string[];
}

/** 살 수 있는 곳 한 곳. label 은 버튼에 쓸 이름(비우면 기본 문구). */
export interface GoodsStore {
  label?: string;
  url: string;
}

/** 게시 문서(엣지 D1 `goods_doc` / 정적 폴백 `/content/goods.json`). 전자책 book_doc 과 동일 패턴. */
export interface GoodsDoc {
  products: GoodsProduct[];
}

// 길이 상한 — 아이 문구는 "한 줄"이 UX 전제(모달 레이아웃이 무너지지 않게), 나머지는 방어적 상한.
export const GOODS_LIMITS = {
  id: 40,
  name: 60,
  tagline: 40, // 기획 25자 내외 + 여유
  desc: 200,
  images: 4,
  tags: 6,
  price: 10_000_000,
  stores: 5, // 살 수 있는 곳 최대 개수(버튼도 이 수만큼)
  storeLabel: 16, // 버튼에 들어갈 이름 — 길면 버튼이 줄바꿈돼 무너진다
} as const;

/** 버튼에 쓸 기본 이름 — 이름을 안 적었을 때. */
export const STORE_DEFAULT_LABEL = "스토어에서 보기";

const ID_RE = /^[a-z0-9_]{1,40}$/;

/** 슬롯 키 규칙 — 관리자 업로드·매니페스트·상세 모달이 같은 함수를 쓴다(문자열 조립 중복 방지). */
export function goodsImageSlot(id: string, n: number): string {
  return `goods.${id}.${n}`;
}

/** 매니페스트 슬롯 키가 이 상품의 것인지(주입·오타 차단). */
export function isGoodsImageSlot(slot: string): boolean {
  return /^goods\.[a-z0-9_]{1,40}\.\d{1,2}$/.test(slot);
}

/**
 * 스토어 URL 안전성 — https만 허용(불변식 7).
 * try/catch 는 의도된 경계: URL 생성자는 잘못된 입력에 throw 하고 플랫폼에 비throw 파서가 없다.
 * 여기서만 삼키고, "안전하지 않음"이라는 **판정 결과로 변환**한다(증상 은폐가 아님).
 */
export function isSafeStoreUrl(url: string): boolean {
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}

/** 유입 분석용 UTM 부착(운영자가 매번 손으로 붙이지 않게). 기존 파라미터는 보존. */
export function withStoreUtm(url: string, itemId: string): string {
  if (!isSafeStoreUrl(url)) return url;
  const u = new URL(url);
  u.searchParams.set("utm_source", "world");
  u.searchParams.set("utm_medium", "goods");
  u.searchParams.set("item", itemId);
  return u.toString();
}

/**
 * 상품명 → 안전 id. 한글은 기계적으로 로마자화할 수 없으므로(번역은 사람/AI의 몫)
 * ASCII 가 없으면 `item_N` 으로 떨어진다. 관리자 폼에서 운영자가 수정 가능해야 한다.
 */
export function goodsIdFromName(name: string, existing: readonly string[] = []): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, GOODS_LIMITS.id);
  const taken = new Set(existing);
  const base = slug.length >= 2 ? slug : `item_${existing.length + 1}`;
  if (!taken.has(base)) return base;
  for (let i = 2; i < 999; i += 1) {
    const candidate = `${base.slice(0, GOODS_LIMITS.id - 4)}_${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base.slice(0, GOODS_LIMITS.id - 4)}_x`;
}

export interface GoodsIssue {
  id?: string;
  field: string;
  message: string;
}

/**
 * 게시 전 검증(관리자) — 운영자가 **고칠 수 있게** 이유를 돌려준다.
 * 웹 렌더 경로는 normalizeGoodsDoc(조용히 걸러내기)을 쓴다. 둘의 역할이 다르다.
 */
export function validateGoodsProduct(p: GoodsProduct, existing: readonly string[] = []): GoodsIssue[] {
  const issues: GoodsIssue[] = [];
  const at = (field: string, message: string) => issues.push({ id: p.id, field, message });

  if (!ID_RE.test(p.id ?? "")) at("id", "id는 영소문자·숫자·밑줄만 사용할 수 있어요.");
  else if (existing.includes(p.id)) at("id", `이미 있는 id예요(${p.id}).`);

  const name = p.name_ko?.trim() ?? "";
  if (!name) at("name_ko", "상품명을 입력해 주세요.");
  else if (name.length > GOODS_LIMITS.name) at("name_ko", `상품명은 ${GOODS_LIMITS.name}자 이내로 써주세요.`);

  if (!p.images?.length) at("images", "이미지를 1장 이상 올려주세요.");
  else {
    if (p.images.length > GOODS_LIMITS.images) at("images", `이미지는 최대 ${GOODS_LIMITS.images}장이에요.`);
    if (!p.images.every(isGoodsImageSlot)) at("images", "이미지 슬롯 키 형식이 올바르지 않아요.");
  }

  if (p.tagline_child_ko && p.tagline_child_ko.length > GOODS_LIMITS.tagline)
    at("tagline_child_ko", `아이 한 줄은 ${GOODS_LIMITS.tagline}자 이내로 써주세요(한 줄이 전제예요).`);
  if (p.desc_guardian_ko && p.desc_guardian_ko.length > GOODS_LIMITS.desc)
    at("desc_guardian_ko", `보호자 설명은 ${GOODS_LIMITS.desc}자 이내로 써주세요.`);

  if (p.store_url && !isSafeStoreUrl(p.store_url)) at("store_url", "스토어 주소는 https:// 로 시작해야 해요.");
  // 살 수 있는 곳 — 넣은 줄만 본다. 빈 줄은 "아직 안 넣은 것"이라 문제가 아니다.
  if (p.stores) {
    if (p.stores.length > GOODS_LIMITS.stores)
      at("stores", `살 수 있는 곳은 최대 ${GOODS_LIMITS.stores}곳까지예요.`);
    p.stores.forEach((s, i) => {
      const url = s?.url?.trim() ?? "";
      if (!url) return;
      if (!isSafeStoreUrl(url)) at(`stores.${i}`, `${i + 1}번째 주소는 https:// 로 시작해야 해요.`);
      else if (isPlaceholderUrl(url, false))
        at(`stores.${i}`, `${i + 1}번째 주소가 아직 예시(REPLACE)예요 — 진짜 주소로 바꿔 주세요.`);
      if ((s.label?.length ?? 0) > GOODS_LIMITS.storeLabel)
        at(`stores.${i}`, `버튼 이름은 ${GOODS_LIMITS.storeLabel}자 이내로 써주세요.`);
    });
  }
  if (p.price !== undefined && (!Number.isInteger(p.price) || p.price < 0 || p.price > GOODS_LIMITS.price))
    at("price", "가격은 0 이상의 정수로 입력해 주세요.");
  if (p.badge && !GOODS_BADGES.includes(p.badge)) at("badge", "뱃지 값이 올바르지 않아요.");

  return issues;
}

function str(v: unknown, max: number): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim();
  return s ? s.slice(0, max) : undefined;
}

/**
 * 게시본 → 렌더 가능한 문서(불변식 7: 외부 입력을 화이트리스트 검증 후 사용).
 * 손상·주입된 항목은 **조용히 제외**한다 — 아이가 보는 화면이 깨지는 것보다 한 칸 비는 게 낫다.
 * (관리자 게시 경로에는 validateGoodsProduct 가 이유를 알려주므로 여기서 삼켜도 진단은 잃지 않는다.)
 */
export function normalizeGoodsDoc(input: unknown): GoodsDoc {
  const raw = (input as { products?: unknown })?.products;
  if (!Array.isArray(raw)) return { products: [] };

  const seen = new Set<string>();
  const products: GoodsProduct[] = [];
  for (const item of raw) {
    const o = item as Record<string, unknown>;
    const id = typeof o?.id === "string" ? o.id : "";
    if (!ID_RE.test(id) || seen.has(id)) continue;

    const name_ko = str(o.name_ko, GOODS_LIMITS.name);
    if (!name_ko) continue;

    const images = Array.isArray(o.images)
      ? o.images.filter((s): s is string => typeof s === "string" && isGoodsImageSlot(s)).slice(0, GOODS_LIMITS.images)
      : [];
    if (!images.length) continue; // 이미지 없는 상품은 상세가 성립하지 않는다

    const price = typeof o.price === "number" && Number.isInteger(o.price) && o.price >= 0 && o.price <= GOODS_LIMITS.price ? o.price : undefined;
    const badge = GOODS_BADGES.includes(o.badge as GoodsBadge) ? (o.badge as GoodsBadge) : undefined;
    // 자리표시자(REPLACE)는 미설정으로 — 아이가 없는 스토어 페이지로 가지 않게
    const store_url =
      typeof o.store_url === "string" && isSafeStoreUrl(o.store_url) && !isPlaceholderUrl(o.store_url, false)
        ? o.store_url
        : undefined;
    const stores = normalizeStores(o.stores);
    const tags = Array.isArray(o.tags)
      ? o.tags.filter((t): t is string => typeof t === "string" && !!t.trim()).map((t) => t.trim().slice(0, 20)).slice(0, GOODS_LIMITS.tags)
      : undefined;

    seen.add(id);
    products.push({
      id,
      name_ko,
      ...(price !== undefined ? { price } : {}),
      ...(badge ? { badge } : {}),
      ...(str(o.tagline_child_ko, GOODS_LIMITS.tagline) ? { tagline_child_ko: str(o.tagline_child_ko, GOODS_LIMITS.tagline) } : {}),
      ...(str(o.desc_guardian_ko, GOODS_LIMITS.desc) ? { desc_guardian_ko: str(o.desc_guardian_ko, GOODS_LIMITS.desc) } : {}),
      images,
      ...(store_url ? { store_url } : {}),
      ...(stores.length ? { stores } : {}),
      ...(o.soldout === true ? { soldout: true } : {}),
      ...(tags?.length ? { tags } : {}),
    });
  }
  return { products };
}

/**
 * 밖에서 온 「살 수 있는 곳」 목록을 다듬는다 — 최대 5곳, https 만, 자리표시자는 버린다.
 *
 * 아이가 **없는 페이지로 가는 일**이 없어야 한다(불변식 7). 주소가 이상한 줄은 통째로 버리고,
 * 남은 것만 버튼이 된다. 그래서 "버튼은 있는데 눌러도 아무 일 없음"이 생기지 않는다.
 */
export function normalizeStores(input: unknown): GoodsStore[] {
  if (!Array.isArray(input)) return [];
  const out: GoodsStore[] = [];
  const seen = new Set<string>();
  for (const raw of input) {
    if (out.length >= GOODS_LIMITS.stores) break;
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const url = typeof o.url === "string" ? o.url.trim() : "";
    if (!url || !isSafeStoreUrl(url) || isPlaceholderUrl(url, false)) continue;
    if (seen.has(url)) continue; // 같은 주소로 버튼 두 개를 만들 이유가 없다
    seen.add(url);
    const label = typeof o.label === "string" ? o.label.trim().slice(0, GOODS_LIMITS.storeLabel) : "";
    out.push(label ? { label, url } : { url });
  }
  return out;
}

/**
 * 이 상품의 버튼 목록 — **화면도 검증도 여기만 본다.**
 *
 * 예전 형식(`store_url` 하나)과 새 형식(`stores` 여럿)을 여기서 합친다. 두 형식을 화면마다
 * 다시 풀면 반드시 갈린다 — 목록에선 버튼이 보이는데 상세에선 안 보이는 식이다.
 * 옛 게시본을 고쳐 쓸 필요도 없다(운영자가 아무것도 안 해도 그대로 동작한다).
 */
export function storeLinks(p: GoodsProduct): GoodsStore[] {
  const list = [...(p.stores ?? [])];
  if (p.store_url && !list.some((s) => s.url === p.store_url)) {
    list.unshift({ url: p.store_url });
  }
  return list
    // ⚠️ **아직 안 채운 주소는 버튼을 만들지 않는다.**
    // 콘텐츠 기본값에는 `https://smartstore.naver.com/REPLACE/products/1` 같은 자리표시자가 들어 있다.
    // 여기서 안 걸러서 아이 화면에 「사러 가기」가 그대로 떠 있었다 — 누르면 없는 가게로 간다
    // (2026-08-22 확인: 게시본이 없어 정적 파일이 그대로 나가는 상태였고 굿즈 6개가 전부 그랬다).
    // 거르면 상세가 스스로 「스토어 준비 중이에요」로 바뀐다 — 이미 그렇게 만들어져 있다.
    // 방어 코드가 아니라 **경계**다: 밖으로 내보내는 주소는 나가기 전에 한 번 본다(불변식 7).
    .filter((s) => !isPlaceholderUrl(s.url))
    .slice(0, GOODS_LIMITS.stores);
}

/** 상세에서 "다른 굿즈 구경하기"에 쓸 이웃(자기 제외, 품절 뒤로). */
export function otherGoods(doc: GoodsDoc, currentId: string, limit = 6): GoodsProduct[] {
  return doc.products
    .filter((p) => p.id !== currentId)
    .sort((a, b) => Number(a.soldout ?? false) - Number(b.soldout ?? false))
    .slice(0, limit);
}

/**
 * 🔴 **편집 화면이 들고 있어야 할 「살 수 있는 곳」 줄.**
 *
 * `storeLinks` 는 **아이에게 보여줄** 목록이라 자리표시자를 걸러 낸다. 편집 화면은 다르다 —
 * 운영자가 적어 둔 것을 **하나도 잃지 않고** 그대로 보여 줘야 한다.
 *
 * ## 무엇을 막나
 * 옛 형식(`store_url` 하나)으로 저장된 상품을 열면 화면에는 그 주소가 첫 줄에 보인다.
 * 그런데 저장할 때 `stores` 만 읽으면 — **운영자가 그 줄을 건드리지 않았을 때** `stores` 가
 * 비어 있어서 **주소가 통째로 사라진다.** 값을 눈으로 보고 있었는데도.
 *
 * 이름을 고치고 저장했을 뿐인데 판매 링크가 없어지고, 아무 오류도 안 난다 —
 * 굿즈가 조용히 「스토어 준비 중」이 된다. 그래서 **읽는 곳과 저장하는 곳이 같은 함수**를 본다.
 */
export function storeRowsOf(p: Pick<GoodsProduct, "stores" | "store_url">): GoodsStore[] {
  if (p.stores) return p.stores;
  return p.store_url ? [{ url: p.store_url }] : [{ url: "" }];
}

/**
 * 🛍️ 이 자리 이름이 **어느 상품의 것인가** — 상품 것이 아니면 `null`.
 *
 * ## 왜 필요한가
 * 상품을 목록에서 지워도 **올린 사진은 매니페스트에 남는다.** 지우는 것은 목록 한 줄이고,
 * 사진은 다른 곳에 있기 때문이다 — 그리고 화면 어디에도 표시가 안 난다.
 * 책에서 똑같은 일이 있었다(2026-08-28: 지운 책의 그림·소리 31개가 남아 있었다).
 *
 * 남는 것 자체는 나쁘지 않다(상품을 다시 만들면 그대로 쓴다). 나쁜 것은 **모르는 것**이다.
 */
export { goodsIdOfSlot } from "./assetOwner.ts";

/**
 * 🛍️ **상품을 지우기 전에 보여줄 말.**
 *
 * 「지울까요?」만 물으면 사진이 어떻게 되는지 알 수 없다. 남는다는 사실과, 그게
 * 쓰레기가 아니라 **다시 쓸 수 있는 것**이라는 점을 같이 말한다.
 */
export function goodsDeletePrompt(name: string, imageCount: number): string {
  return [
    `'${name}' 상품을 목록에서 지울까요?`,
    "",
    imageCount > 0
      ? `올린 사진 ${imageCount}장은 **지워지지 않고 남아요.**\n같은 이름으로 다시 만들면 그대로 붙어요.`
      : "이 상품에 올린 사진은 없어요.",
    "",
    "게시해야 아이 화면에 반영돼요.",
  ].join("\n");
}
